/**
 * Floating-island zone edges (AD-840; docs/architecture/island-edges.md).
 *
 * Every open-world zone used to end in its bounds RECTANGLE: a straight cut into
 * nothing. A zone is now a floating island of The Great Cloud, and its walkable
 * edge is an irregular coastline (`IslandOutline`, collision.ts) generated
 * deterministically from the zone's bounds, a seed, two shape knobs and the
 * things that must stand on it (keep-ins: spawns, shrines, NPCs, nodes, enemy
 * homes, settlement pieces).
 *
 * Two halves, deliberately split by who runs them:
 *
 * - **Generation** (`generateIslandOutline`) runs on the SERVER only, at zone
 *   build. It uses `Math.sin`/`Math.cos`/`Math.pow`, which no engine promises to
 *   round identically, so its output is quantised to cm and SHIPPED on the join
 *   ack (`WorldDescriptor.collision.outline`) â€” the client never regenerates it.
 * - **Clamping** (`insideOutline`, `clampToOutline`, `clampToZone`) runs on BOTH
 *   sides, every movement step (movement.ts, mobility.ts). It uses only `+ - * /`,
 *   comparisons and `Math.sqrt` (IEEE-754 correctly rounded), over the shipped
 *   points, so client prediction and server integration agree bit for bit
 *   (AD-026's single-implementation rule).
 *
 * The outline is star-shaped about its centre by construction (one vertex per
 * angle step, positive radii), which is what makes the clamp simple: a point on
 * the boundary pulled a skin toward the centre is strictly inside.
 */
// ---------------------------------------------------------------------------
// Clamping (shared, deterministic: arithmetic + sqrt only)
// ---------------------------------------------------------------------------
/** How far inside the coastline a clamped point is left, metres (strictly inside). */
export const ISLAND_CLAMP_SKIN_M = 1e-3;
/**
 * Even-odd point-in-polygon. A point exactly on an edge may read either way; the
 * clamp below never leaves a point there (it pulls a skin inside).
 */
export function insideOutline(outline, x, z) {
    const pts = outline.points;
    const n = pts.length;
    if (n < 3)
        return true;
    let inside = false;
    let j = n - 1;
    for (let i = 0; i < n; i += 1) {
        const a = pts[i];
        const b = pts[j];
        if (a.z > z !== b.z > z) {
            const xCross = a.x + ((z - a.z) * (b.x - a.x)) / (b.z - a.z);
            if (x < xCross)
                inside = !inside;
        }
        j = i;
    }
    return inside;
}
/** Closest point on the coastline to (x, z), and its squared distance. */
export function nearestOnOutline(outline, x, z) {
    const pts = outline.points;
    const n = pts.length;
    let bx = x;
    let bz = z;
    let best = Number.POSITIVE_INFINITY;
    for (let i = 0; i < n; i += 1) {
        const a = pts[i];
        const b = pts[(i + 1) % n];
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const len2 = dx * dx + dz * dz;
        let t = len2 > 1e-12 ? ((x - a.x) * dx + (z - a.z) * dz) / len2 : 0;
        if (t < 0)
            t = 0;
        else if (t > 1)
            t = 1;
        const qx = a.x + t * dx;
        const qz = a.z + t * dz;
        const ex = x - qx;
        const ez = z - qz;
        const d2 = ex * ex + ez * ez;
        if (d2 < best) {
            best = d2;
            bx = qx;
            bz = qz;
        }
    }
    return { x: bx, z: bz, d2: best };
}
/**
 * Keep (x, z) on the island: unchanged when inside, else the nearest coastline
 * point pulled {@link ISLAND_CLAMP_SKIN_M} toward the centre (strictly inside,
 * because the outline is star-shaped about it). Walking into the edge therefore
 * SLIDES along the coast, exactly like walking into a wall.
 */
export function clampToOutline(outline, x, z) {
    if (outline.points.length < 3 || insideOutline(outline, x, z))
        return { x, z };
    const q = nearestOnOutline(outline, x, z);
    const dx = outline.cx - q.x;
    const dz = outline.cz - q.z;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (!(len > ISLAND_CLAMP_SKIN_M))
        return { x: outline.cx, z: outline.cz };
    return { x: q.x + (dx / len) * ISLAND_CLAMP_SKIN_M, z: q.z + (dz / len) * ISLAND_CLAMP_SKIN_M };
}
/**
 * The whole zone boundary: the bounds rectangle, then the coastline when the
 * zone has one. The one call every non-step placement uses (join, click-to-move,
 * boss adds), so they can never disagree with the walk.
 */
export function clampToZone(bounds, collider, x, z) {
    let cx = x < bounds.minX ? bounds.minX : x > bounds.maxX ? bounds.maxX : x;
    let cz = z < bounds.minZ ? bounds.minZ : z > bounds.maxZ ? bounds.maxZ : z;
    const outline = collider?.outline;
    if (outline !== undefined)
        ({ x: cx, z: cz } = clampToOutline(outline, cx, cz));
    return { x: cx, z: cz };
}
// ---------------------------------------------------------------------------
// Generation (server-side; quantised + shipped, never regenerated by a client)
// ---------------------------------------------------------------------------
/** Vertices on a generated coastline (one per 2.8Â°). */
export const ISLAND_OUTLINE_VERTICES = 128;
/** Defaults for the content knobs (content `BiomeDef.island`; island-edges.md Â§3). */
export const ISLAND_DEFAULTS = {
    /** Fraction of the radius the coastline may bite in (0 = a rounded rectangle). */
    roughness: 0.14,
    /** Superellipse exponent rounding the bounds corners (2 = ellipse, large = the rectangle). */
    cornerRound: 3.2,
    /** Drawn ground beyond the walkable line before the lip, metres. */
    rimMargin: 1.6,
    /** Rim crag/debris density multiplier. */
    crags: 1,
    /** Hanging roots under the rim. */
    roots: true,
    /** Depth of the cloud sea below the rim, metres (0 = none). */
    cloudSeaDepth: 42,
    /** Distant islands drawn for depth. */
    distantIslands: 4,
};
/** Content bounds on the knobs (validator + generator share them). */
export const ISLAND_LIMITS = {
    roughness: [0, 0.3],
    cornerRound: [2, 12],
    rimMargin: [0.5, 6],
    crags: [0, 2],
    undersideDepth: [4, 200],
    cloudSeaDepth: [0, 400],
    distantIslands: [0, 8],
};
/** mulberry32 â€” a tiny, well-mixed 32-bit PRNG (deterministic per seed). */
export function islandRng(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** FNV-1a of a string â†’ uint32: the seed of a zone that authors none. */
export function islandSeedOf(text) {
    let h = 2166136261;
    for (let i = 0; i < text.length; i += 1)
        h = Math.imul(h ^ text.charCodeAt(i), 16777619) >>> 0;
    return h >>> 0;
}
const round2 = (v) => Math.round(v * 100) / 100;
/** Distance from (x, z) to the rectangle's nearest edge (â‰¥ 0 inside). */
function distToBoundsEdge(b, x, z) {
    return Math.min(x - b.minX, b.maxX - x, z - b.minZ, b.maxZ - z);
}
/**
 * How much clear ground the coastline leaves around a keep-in: inside, and at
 * least `radius` from the coast â€” or as far as the bounds rectangle allows (a
 * keep-in authored near the rectangle's edge can only be given what the
 * rectangle has; the coastline then runs along the rectangle there).
 */
export function keepInDeficit(outline, bounds, k) {
    const need = Math.max(0, Math.min(k.radius, distToBoundsEdge(bounds, k.x, k.z) - 0.05));
    if (!insideOutline(outline, k.x, k.z))
        return need + Math.sqrt(nearestOnOutline(outline, k.x, k.z).d2);
    const have = Math.sqrt(nearestOnOutline(outline, k.x, k.z).d2);
    return Math.max(0, need - have);
}
/**
 * Generate a zone's walkable coastline. Pure and deterministic for a given input
 * on one engine (the server); the result is quantised to cm.
 *
 * Shape: a superellipse fitted to the bounds (rounds the rectangle's corners),
 * bitten in by up to `roughness` of its radius along a smooth low-harmonic noise
 * (bays and headlands) plus a small per-vertex jitter (a rocky coast), never
 * outside the rectangle. Then every keep-in is guaranteed its clear radius: the
 * coast is pushed out as a smooth lobe around it (a cone falloff over the
 * neighbouring vertices), verified on the actual polygon, and repeated until
 * every keep-in passes or the rectangle is the limit.
 */
export function generateIslandOutline(input) {
    const b = input.bounds;
    const n = Math.max(16, Math.floor(input.vertices ?? ISLAND_OUTLINE_VERTICES));
    const roughness = clampNum(input.roughness ?? ISLAND_DEFAULTS.roughness, 0, 0.3);
    const p = clampNum(input.cornerRound ?? ISLAND_DEFAULTS.cornerRound, 2, 12);
    const keepIns = input.keepIns ?? [];
    const cx = (b.minX + b.maxX) / 2;
    const cz = (b.minZ + b.maxZ) / 2;
    const hx = (b.maxX - b.minX) / 2;
    const hz = (b.maxZ - b.minZ) / 2;
    const rng = islandRng(input.seed);
    // Smooth bays/headlands: harmonics 2..8 with 1/k amplitudes, random phases.
    const harmonics = [];
    let ampSum = 0;
    for (let k = 2; k <= 8; k += 1) {
        const amp = (0.55 + 0.45 * rng()) / k;
        harmonics.push({ k, amp, phase: rng() * Math.PI * 2 });
        ampSum += amp;
    }
    const theta = [];
    const r = [];
    const rMax = [];
    for (let i = 0; i < n; i += 1) {
        const t = (i / n) * Math.PI * 2;
        theta.push(t);
        const c = Math.cos(t);
        const s = Math.sin(t);
        const ac = Math.abs(c);
        const as = Math.abs(s);
        const rRect = Math.min(ac > 1e-9 ? hx / ac : Infinity, as > 1e-9 ? hz / as : Infinity);
        const rSe = Math.pow(Math.pow(ac / hx, p) + Math.pow(as / hz, p), -1 / p);
        let noise = 0;
        for (const h of harmonics)
            noise += h.amp * Math.cos(h.k * t + h.phase);
        noise /= ampSum; // [-1, 1]
        const jitter = rng() - 0.5;
        const bite = roughness * (0.55 + 0.45 * noise) + roughness * 0.3 * jitter;
        const floor = 0.35 * Math.min(hx, hz);
        rMax.push(rRect);
        r.push(Math.min(rRect, Math.max(floor, rSe * (1 - bite))));
    }
    const build = () => ({
        cx: round2(cx),
        cz: round2(cz),
        points: r.map((radius, i) => ({
            x: round2(cx + radius * Math.cos(theta[i])),
            z: round2(cz + radius * Math.sin(theta[i])),
        })),
    });
    // Keep-ins: push the coast out around each, then verify on the real polygon.
    const want = keepIns.map((k) => k.radius);
    const step = (Math.PI * 2) / n;
    for (let pass = 0; pass < 12 && keepIns.length > 0; pass += 1) {
        const req = new Array(n).fill(0);
        keepIns.forEach((k, ki) => {
            const rho = want[ki];
            const dx = k.x - cx;
            const dz = k.z - cz;
            const d = Math.sqrt(dx * dx + dz * dz);
            const phi = Math.atan2(dz, dx);
            for (let i = 0; i < n; i += 1) {
                let delta = theta[i] - phi;
                delta = Math.atan2(Math.sin(delta), Math.cos(delta));
                const along = d * Math.cos(delta);
                const perp = d * Math.sin(delta);
                if (Math.abs(perp) >= rho)
                    continue;
                const t = along + Math.sqrt(rho * rho - perp * perp);
                if (t > req[i])
                    req[i] = t;
            }
        });
        // Cone falloff: a lobe, not a spike â€” the coast eases back over the neighbours.
        for (let i = 0; i < n; i += 1) {
            let lifted = req[i];
            for (let j = 0; j < n; j += 1) {
                const rj = req[j];
                if (rj <= 0 || j === i)
                    continue;
                const di = Math.min(Math.abs(i - j), n - Math.abs(i - j));
                const v = rj - 0.6 * di * step * rj;
                if (v > lifted)
                    lifted = v;
            }
            if (lifted > r[i])
                r[i] = Math.min(rMax[i], lifted);
        }
        const outline = build();
        let ok = true;
        keepIns.forEach((k, ki) => {
            const deficit = keepInDeficit(outline, b, k);
            if (deficit > 0.02) {
                ok = false;
                want[ki] = want[ki] + deficit + 0.25;
            }
        });
        if (ok)
            return outline;
    }
    return build();
}
/** The drawn rim: the walkable coastline pushed `margin` metres out from the centre. */
export function islandRimPolygon(outline, margin) {
    return outline.points.map((pt) => {
        const dx = pt.x - outline.cx;
        const dz = pt.z - outline.cz;
        const len = Math.sqrt(dx * dx + dz * dz);
        if (!(len > 1e-9))
            return { x: pt.x, z: pt.z };
        return { x: pt.x + (dx / len) * margin, z: pt.z + (dz / len) * margin };
    });
}
function clampNum(v, lo, hi) {
    if (!Number.isFinite(v))
        return lo;
    return v < lo ? lo : v > hi ? hi : v;
}
//# sourceMappingURL=island.js.map